import React from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useTheme, type ColorTheme, GLOW_MULTIPLIERS } from '@/providers/ThemeProvider';
import { cn } from '@flowstate/utils';
import DarkVeil from '@/components/DarkVeil';
import Threads from '@/components/Threads';
import { ThemeBackground } from '@/components/ui/ThemeBackground';
import { ClientMyceliumScene } from '@/features/landing/components/ClientMyceliumScene';

/**
 * Sanitize URL to prevent XSS attacks via javascript: protocol.
 * Only allows http, https, and blob URLs (NOT data: URLs for security).
 */
function sanitizeImageUrl(url: string): string {
  const trimmed = url.trim();
  const isValidProtocol = /^(https?|blob):/.test(trimmed);
  if (!isValidProtocol) return '';

  // Additional validation for blob URLs
  if (trimmed.startsWith('blob:')) {
    try {
      new URL(trimmed);
    } catch {
      return '';
    }
  }
  return trimmed;
}

/**
 * Global site background component that renders dynamic effects
 * Connected to the application's color theme management
 * Switches between DarkVeil (Dark Mode) and Threads (Light Mode)
 */
const SiteBackground: React.FC = () => {
  const router = useRouterState();
  const pathname = router.location.pathname;
  const isLandingPage = pathname === '/' || pathname === '/home' || pathname.startsWith('/home/');

  const {
    colorTheme,
    theme,
    glowIntensity,
    performanceMode,
    backgroundType,
    backgroundImages
  } = useTheme();

  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

  // Reset index when images change to prevent out of bounds
  React.useEffect(() => {
    setCurrentImageIndex(0);
  }, [backgroundImages]);
  
  // Clamp index if images array shrinks
  React.useEffect(() => {
    setCurrentImageIndex(prev => {
      if (prev >= backgroundImages.length && backgroundImages.length > 0) {
        return backgroundImages.length - 1;
      }
      return prev;
    });
  }, [backgroundImages.length]);

  // Slideshow logic
  React.useEffect(() => {
    if (backgroundType === 'slideshow' && backgroundImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % backgroundImages.length);
      }, 10000); // 10 seconds per image
      return () => clearInterval(interval);
    } else if (backgroundType === 'slideshow' && backgroundImages.length === 1) {
      // Reset to 0 if only one image
      setCurrentImageIndex(0);
    }
    return;
  }, [backgroundType, backgroundImages.length]);

  const multiplier = GLOW_MULTIPLIERS[glowIntensity] || 1.0;

  // Determine if we are in dark mode with reactive system theme detection
  const [isDark, setIsDark] = React.useState(() => {
    if (theme === 'dark') {return true;}
    if (theme === 'light') {return false;}
    // System theme - check media query
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  // Listen for system theme changes when in system mode
  React.useEffect(() => {
    if (theme !== 'system') {
      setIsDark(theme === 'dark');
      return;
    }

    // Set up media query listener for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsDark(e.matches);
    };

    // Only update if different to prevent unnecessary re-renders
    if (isDark !== mediaQuery.matches) {
      setIsDark(mediaQuery.matches);
    }

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [theme, isDark]);

  const [renderDark, setRenderDark] = React.useState(isDark);
  const [renderLight, setRenderLight] = React.useState(!isDark);

  React.useEffect(() => {
    if (isDark) {
      setRenderDark(true);
      const timer = setTimeout(() => setRenderLight(false), 500);
      return () => clearTimeout(timer);
    } else {
      setRenderLight(true);
      const timer = setTimeout(() => setRenderDark(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isDark]);

  // Map color themes to hue shift values for DarkVeil (Dark Mode)
  const getHueShift = (theme: ColorTheme): number => {
    switch (theme) {
      case 'flowstate-blue':
        return -65; // 220 - 285
      case 'purple-edge':
        return -15; // 270 - 285
      case 'emerald':
        return -135; // 150 - 285
      case 'amber':
        return -250; // 35 - 285
      case 'rose':
        return 55; // 340 - 285
      default:
        return -65;
    }
  };

  // Map color themes to RGB values for Threads (Light Mode)
  const getThreadsColor = (theme: ColorTheme): [number, number, number] => {
    switch (theme) {
      case 'flowstate-blue':
        return [0.23, 0.56, 0.98];
      case 'purple-edge':
        return [0.55, 0.36, 0.96];
      case 'emerald':
        return [0.18, 0.7, 0.49];
      case 'amber':
        return [0.96, 0.64, 0.11];
      case 'rose':
        return [0.96, 0.2, 0.44];
      default:
        return [0.23, 0.56, 0.98];
    }
  };

  if (isLandingPage) {
    return null;
  }

  return (
    <div
      id="global-site-bg"
      className="fixed inset-0 w-screen h-screen pointer-events-none overflow-hidden"
      style={{ zIndex: -10, isolation: 'isolate' }}
    >
      {(backgroundType === 'logo' ||
        (backgroundType === 'image' && backgroundImages.length === 0) ||
        (backgroundType === 'slideshow' && backgroundImages.length === 0)) && (
        <ThemeBackground variant="gradient" logoOpacity={0.4} logoScale={0.7} />
      )}

      {backgroundType === 'webgl' && !performanceMode && (
        <>
          {renderDark && (
            <div
              className={cn(
                "absolute inset-0 w-full h-full mix-blend-screen transition-opacity duration-500",
                isDark ? "opacity-60" : "opacity-0"
              )}
              style={{
                filter: `contrast(${0.8 + multiplier * 0.2}) brightness(${0.8 + multiplier * 0.2})`,
                transform: 'translateZ(0)',
              }}
            >
              <DarkVeil
                hueShift={getHueShift(colorTheme)}
                noiseIntensity={0}
                scanlineIntensity={1}
                speed={1.5}
                scanlineFrequency={0}
                warpAmount={0}
                resolutionScale={1.5}
              />
            </div>
          )}
          {renderLight && (
            <div
              className={cn(
                "absolute inset-0 w-full h-full transition-opacity duration-500",
                !isDark ? "opacity-30" : "opacity-0"
              )}
              style={{
                filter: `saturate(${0.8 + multiplier * 0.4})`,
                transform: 'translateZ(0)',
              }}
            >
              <Threads
                color={getThreadsColor(colorTheme)}
                amplitude={3}
                distance={0.2}
                enableMouseInteraction
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          )}
        </>
      )}

      {backgroundType === 'mycelium' && !performanceMode && (
        <div
          className={cn(
            "absolute inset-0 w-full h-full transition-opacity duration-500",
            isDark ? "opacity-50" : "opacity-30"
          )}
          style={{
            filter: `contrast(${0.8 + multiplier * 0.2}) brightness(${0.8 + multiplier * 0.2})`,
            transform: 'translateZ(0)',
          }}
        >
          <ClientMyceliumScene className="h-full w-full" />
        </div>
      )}

      {backgroundType === 'image' && backgroundImages.length > 0 && backgroundImages[0] && sanitizeImageUrl(backgroundImages[0]) && (
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center transition-opacity duration-1000"
          style={{
            backgroundImage: `url("${sanitizeImageUrl(backgroundImages[0])}")`,
          }}
        />
      )}

      {backgroundType === 'slideshow' && backgroundImages.length > 0 && (
        <div className="absolute inset-0 w-full h-full">
          {backgroundImages
            .filter(img => sanitizeImageUrl(img))
            .map((img, index) => {
              const sanitizedUrl = sanitizeImageUrl(img);
              return (
                <div
                  key={img}
                  className={cn(
                    "absolute inset-0 w-full h-full bg-cover bg-center transition-opacity duration-2000 opacity-0",
                    index === currentImageIndex && "opacity-100"
                  )}
                  style={{
                    backgroundImage: `url("${sanitizedUrl}")`,
                  }}
                />
              );
            })}
        </div>
      )}

      {/* Overlay and grid only when not using custom images or logo watermark (webgl/none) */}
      {backgroundType !== 'image' && backgroundType !== 'slideshow' && backgroundType !== 'logo' && (
        <>
          <div className="absolute inset-0 bg-background/5" aria-hidden />
          <div className="absolute inset-0 bg-grid-slate-900/[0.04] dark:bg-grid-slate-400/[0.05] bg-[size:40px_40px]" aria-hidden />
        </>
      )}
    </div>
  );
};

export default SiteBackground;
